import {
  evaluateAllConditions,
  evaluateAnyConditionGroup,
  evaluateSingleCondition
} from './evaluateCondition'
import type { FlowStepConfig } from './types'

/**
 * Determines whether a field is currently visible given the flattened form values.
 * A field is visible when all applicable visibility conditions are satisfied.
 * Fields without any condition are always considered visible.
 */
function isFieldVisible(
  field: FlowStepConfig['fields'][number],
  allValues: Record<string, unknown>
): boolean {
  if (field.visibleWhen && !evaluateSingleCondition(field.visibleWhen, allValues)) return false
  if (field.visibleWhenAll && !evaluateAllConditions(field.visibleWhenAll, allValues)) return false
  if (field.visibleWhenAny && !evaluateAnyConditionGroup(field.visibleWhenAny, allValues))
    return false
  return true
}

/**
 * Finds the first step (before currentStepId) that has required, visible fields not yet filled.
 * Returns the FlowStepConfig of that step, or null if all previous required steps are complete.
 *
 * A field is considered filled when its value is not undefined, null, empty string, or false.
 * Conditional fields (visibleWhen / visibleWhenAll / visibleWhenAny) are only checked
 * when their visibility condition is satisfied given the current form data.
 */
export function findFirstIncompleteStep(
  steps: FlowStepConfig[],
  formData: Record<string, Record<string, unknown>>,
  currentStepId: number
): FlowStepConfig | null {
  // Flatten all form data so cross-step visibility conditions can be evaluated
  const allValues = Object.values(formData).reduce<Record<string, unknown>>((acc, stepData) => {
    return stepData && typeof stepData === 'object' ? { ...acc, ...stepData } : acc
  }, {})

  const previousSteps = steps.filter((s) => s.id < currentStepId).sort((a, b) => a.id - b.id)

  for (const step of previousSteps) {
    const stepKey = `step${step.id}`
    const stepData = formData[stepKey] ?? {}

    const hasUnfilledRequired = step.fields.some((field) => {
      if (!field.required) return false
      if (!isFieldVisible(field, { ...allValues, ...stepData })) return false
      const value = stepData[field.key]
      return value === undefined || value === null || value === '' || value === false
    })

    if (hasUnfilledRequired) {
      return step
    }
  }

  return null
}
